import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PromptManager from '../PromptManager';
import AiTrackingProjects from '../projects';
import MessageCustom from '../../../components/MessageCustom';
import { getProjects } from '../create/query';
import OnboardingFlow from '../onboarding/OnboardingFlow';
import SetupChecklist from '../onboarding/SetupChecklist';
import WelcomeModal from '../onboarding/WelcomeModal';
import {
    dismissWelcome,
    getProjectSetupState,
    pickOnboardingProject,
    shouldShowWelcome,
} from '../onboarding/state';

class AiTrackingResults extends Component {
    constructor(props) {
        super(props);

        this.addActive = this.addActive.bind(this);
        this.onChangeTag = this.onChangeTag.bind(this);
        this.getProjectsData = this.getProjectsData.bind(this);
        this.setProjects = this.setProjects.bind(this);
        this.onStartSetup = this.onStartSetup.bind(this);
        this.onSkipWelcome = this.onSkipWelcome.bind(this);
        this.onOpenStep = this.onOpenStep.bind(this);
        this.onCloseFlow = this.onCloseFlow.bind(this);
        this.onProjectChanged = this.onProjectChanged.bind(this);

        this.url = new URL(window.location);
        this.urlParams = new URLSearchParams(window.location.search);

        this.tab = this.urlParams.get('tab');

        const messages = sessionStorage.getItem('ai_tracking_message')
            ? JSON.parse(sessionStorage.getItem('ai_tracking_message'))
            : [];
        sessionStorage.removeItem('ai_tracking_message');

        this.state = {
            tag: this.tab || 'project_manager',
            messages,
            projects: [],
            loading: false,
            showWelcome: false,
            // The step the flow is open at, or null when it is closed.
            flowStep: null,
        };
    }

    /** The project setup is about: the first unfinished one, default project first. */
    onboardingProject() {
        return pickOnboardingProject(this.state.projects);
    }

    onStartSetup() {
        const project = this.onboardingProject();
        dismissWelcome();
        this.setState({
            showWelcome: false,
            flowStep: project ? getProjectSetupState(project).nextStep : null,
        });
    }

    /** Skipping is final: the checklist in the header is how the user comes back. */
    onSkipWelcome() {
        dismissWelcome();
        this.setState({ showWelcome: false });
    }

    onOpenStep(step) {
        this.setState({ flowStep: step });
    }

    /** A step saved something, so the row the page is already showing is out of date. */
    onProjectChanged(project) {
        if (!project) return;
        this.setState((prevState) => ({
            projects: prevState.projects.map((item) =>
                item.id === project.id ? { ...item, ...project } : item,
            ),
        }));
    }

    /**
     * Leaving the flow, at whatever point.
     *
     * The project is folded back in first so the checklist is correct immediately, then refetched
     * because a step may have changed more than it reported back -- generated prompts carry
     * details only the server has.
     */
    onCloseFlow(project) {
        this.onProjectChanged(project);
        this.setState({ flowStep: null });
        this.getProjectsData();
    }

    addActive(value) {
        return this.state.tag === value;
    }

    onChangeTag(value) {
        this.setState({
            tag: value,
        });

        this.url = new URL(window.location);
        this.url.searchParams.set('tab', value);
        history.pushState({}, '', this.url);
    }

    async getProjectsData() {
        try {
            this.setState({
                loading: true,
            });
            const response = await getProjects(true);
            const data = await response.json();
            this.setState((prevState) => ({
                projects: data,
                loading: false,
                // Offered once, and only while the flow is not already running.
                showWelcome:
                    prevState.flowStep === null && !prevState.showWelcome
                        ? shouldShowWelcome(data)
                        : prevState.showWelcome,
            }));
        } catch (err) {
            console.log('err', err);
            this.setState({
                loading: false,
            });
        }
    }

    componentDidMount() {
        this.getProjectsData();
    }

    setProjects(projects) {
        this.setState({
            projects,
        });
    }

    render() {
        const { t } = this.props;

        const tabs = (t) => [
            { id: 'project_manager', label: 'Projects' },
            { id: 'prompt_manager', label: 'Prompts' },
        ];

        return (
            <div>
                {this.state.messages?.length > 0 && (
                    <div className="my-10">
                        {this.state.messages?.map((item, index) => (
                            <MessageCustom
                                key={index}
                                message={item.message}
                                message_type={item.message_type}
                                link={item.link}
                                link_name={item.link_name}
                            />
                        ))}
                    </div>
                )}
                <SetupChecklist project={this.onboardingProject()} onOpenStep={this.onOpenStep} />
                <div className="main tags-container">
                    {tabs(t).map((tab) => (
                        <span
                            className={`tab-item ${this.addActive(tab.id) && 'active-tag'}`}
                            key={tab.id}
                            onClick={() => this.onChangeTag(tab.id)}
                        >
                            {tab.label}
                        </span>
                    ))}
                </div>
                <div style={{ display: this.state.tag === 'prompt_manager' ? 'block' : 'none' }}>
                    <PromptManager
                        projects={this.state.projects}
                        projectLoader={this.state.loading}
                        setProjects={this.setProjects}
                        getProjectsData={this.getProjectsData}
                    />
                </div>
                <div style={{ display: this.state.tag === 'project_manager' ? 'block' : 'none' }}>
                    <AiTrackingProjects
                        projects={this.state.projects}
                        loading={this.state.loading}
                        setProjects={this.setProjects}
                        getProjectsData={this.getProjectsData}
                    />
                </div>
                <WelcomeModal
                    isOpen={this.state.showWelcome}
                    onStart={this.onStartSetup}
                    onSkip={this.onSkipWelcome}
                />
                {this.state.flowStep !== null && (
                    <OnboardingFlow
                        project={this.onboardingProject()}
                        initialStep={this.state.flowStep}
                        onProjectChanged={this.onProjectChanged}
                        onClose={this.onCloseFlow}
                    />
                )}
            </div>
        );
    }
}
export default withTranslation()(AiTrackingResults);
